// client/src/components/pages/Settings/LexiStarWebhooks/webhooksApi.ts
//
// LexiStar webhook API client (ST-00207; M4b contracts §3.2). Server:
// server/modules/lexistar/webhooks, mounted at /api/lexistar/webhooks. Thin wrapper over the
// shared axios instance in services/api (cookie session, CSRF header, 401 refresh; `api`
// already carries `/api`); resolves to the response BODY.
//
//   listSubscriptions()                 → { subscriptions, key_configured }
//   createSubscription(input)           → { subscription, secret }   (the secret once)
//   updateSubscription(id, patch)       → subscription               (active false = pause)
//   deleteSubscription(id)
//   rotateSecret(id)                    → { subscription, secret }
//   sendTest(id)                        → { delivery }               (PING)
//   resumeSubscription(id)              → subscription
//   listDeliveries(query)               → { deliveries, has_more }
//   getDelivery(id)                     → delivery + payload + envelope
//   replayDelivery(id)                  → { delivery, waits_for_resume }
import api from '../../../../services/api';
import type {
  WebhookDeliveryDetail,
  WebhookDeliveryList,
  WebhookDeliveryQuery,
  WebhookDeliverySummary,
  WebhookSubscription,
  WebhookSubscriptionInput,
  WebhookSubscriptionList,
  WebhookSubscriptionPatch,
  WebhookSubscriptionWithSecret,
} from '../../../../types/lexistar-webhooks';

const BASE = '/lexistar/webhooks';
const sub = (id: string): string => `${BASE}/subscriptions/${encodeURIComponent(id)}`;

export async function listSubscriptions(): Promise<WebhookSubscriptionList> {
  const res = await api.get(`${BASE}/subscriptions`);
  return res.data as WebhookSubscriptionList;
}

export async function createSubscription(
  input: WebhookSubscriptionInput
): Promise<WebhookSubscriptionWithSecret> {
  const res = await api.post(`${BASE}/subscriptions`, input);
  return res.data as WebhookSubscriptionWithSecret;
}

export async function updateSubscription(
  id: string,
  patch: WebhookSubscriptionPatch
): Promise<WebhookSubscription> {
  const res = await api.patch(sub(id), patch);
  return res.data as WebhookSubscription;
}

export async function deleteSubscription(id: string): Promise<void> {
  await api.delete(sub(id));
}

export async function rotateSecret(id: string): Promise<WebhookSubscriptionWithSecret> {
  const res = await api.post(`${sub(id)}:rotate`, {});
  return res.data as WebhookSubscriptionWithSecret;
}

export async function sendTest(id: string): Promise<{ delivery: WebhookDeliverySummary }> {
  const res = await api.post(`${sub(id)}:test`, {});
  return res.data as { delivery: WebhookDeliverySummary };
}

export async function resumeSubscription(id: string): Promise<WebhookSubscription> {
  const res = await api.post(`${sub(id)}:resume`, {});
  return res.data as WebhookSubscription;
}

export async function listDeliveries(
  query: WebhookDeliveryQuery = {}
): Promise<WebhookDeliveryList> {
  const params = Object.fromEntries(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== null && v !== '')
  );
  const res = await api.get(`${BASE}/deliveries`, { params });
  return res.data as WebhookDeliveryList;
}

export async function getDelivery(id: string): Promise<WebhookDeliveryDetail> {
  const res = await api.get(`${BASE}/deliveries/${encodeURIComponent(id)}`);
  return res.data as WebhookDeliveryDetail;
}

export async function replayDelivery(
  id: string
): Promise<{ delivery: WebhookDeliverySummary; waits_for_resume: boolean }> {
  const res = await api.post(`${BASE}/deliveries/${encodeURIComponent(id)}:replay`, {});
  return res.data as { delivery: WebhookDeliverySummary; waits_for_resume: boolean };
}

/** The server's message of a failed call (validation details joined), else `fallback`. */
export function errorText(err: unknown, fallback: string): string {
  const data = (
    err as { response?: { data?: { error?: string; message?: string; details?: string[] } } }
  )?.response?.data;
  if (data?.details?.length) return data.details.join('; ');
  return data?.error || data?.message || fallback;
}
